import { LoadingState, QueryError } from '#/components/common/loading-state'
/**
 * `/playbooks` — Parity P5 (Herdr plugin marketplace / Fractal registry + `defaultPrompt`).
 * Built-in Indonesian recipes plus community manifests from GitHub repos tagged `bystep-playbook`.
 * Landing-styled (`.mk`, light-only); plain words — a non-technical owner reads this page.
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { Container, MkFooter, MkLink, MkNav, Overline, SectionHead } from '#/components/landing/mk'
import { usePlaybooks, type PlaybookDto } from '#/lib/api-playbooks'
import { cn } from '#/lib/utils'
import { getLocale } from '#/paraglide/runtime'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/playbooks')({
  head: () => ({
    meta: [
      { title: m.pb_meta_title() },
      { name: 'description', content: m.pb_meta_desc() },
      { property: 'og:title', content: m.pb_meta_title() },
      { property: 'og:description', content: m.pb_meta_desc() },
    ],
  }),
  component: Page,
})

const RULE_TONE = { always: 'text-status-done', never: 'text-status-failed', ask: 'text-status-progress' } as const

function Card({ p, locale }: { p: PlaybookDto; locale: 'en' | 'id' }) {
  const community = p.source === 'community'
  return (
    <article className="mk-card-white flex flex-col gap-3 p-6" data-testid="playbook-card" data-playbook={p.id}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0">{p.name[locale]}</h3>
        <span
          className={cn('shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide', community ? 'border-status-progress/50 text-status-progress' : 'border-[var(--mk-border)] text-[var(--mk-fg-muted)]')}
          title={community ? m.pb_badge_community_hint() : undefined}
        >
          {community ? m.pb_badge_community() : m.pb_badge_bystep()}
        </span>
      </div>
      <p className="mk-muted text-[15px] leading-relaxed">{p.desc[locale]}</p>
      {p.examples[locale]?.length ? (
        <div>
          <div className="text-[12px] font-semibold uppercase tracking-wide text-[var(--mk-fg-muted)]">{m.pb_examples()}</div>
          <ul className="mt-1.5 space-y-1 text-[14px]">
            {p.examples[locale].map((e) => (
              <li key={e} className="flex gap-2">
                <span aria-hidden="true">•</span>
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {p.rules.length ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={m.pb_rules()}>
          {p.rules.map((r) => (
            <li key={r.templateId} className={cn('rounded-full border border-[var(--mk-border)] px-2 py-0.5 text-[12px]', RULE_TONE[r.kind])} title={r.text[locale]}>
              {r.kind === 'always' ? m.pb_rule_always() : r.kind === 'never' ? m.pb_rule_never() : m.pb_rule_ask()} · {r.text[locale]}
            </li>
          ))}
        </ul>
      ) : null}
      {p.tags.length ? (
        <ul className="flex flex-wrap gap-1.5">
          {p.tags.map((tag) => (
            <li key={tag} className="rounded-full border border-[var(--mk-border)] px-2 py-0.5 text-[12px] text-[var(--mk-fg-muted)]">
              {tag}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-auto flex items-center justify-between gap-3 border-t border-[var(--mk-border)] pt-3">
        {community && p.repo ? (
          <a href={`https://github.com/${p.repo}`} target="_blank" rel="noreferrer" className="truncate text-[13px] text-[var(--mk-fg-muted)] underline-offset-2 hover:underline">
            {p.repo}
          </a>
        ) : (
          <span className="text-[13px] text-[var(--mk-fg-muted)]">{m.pb_by_bystep()}</span>
        )}
        <MkLink to="/plan" search={{ flow: 'new', kit: p.kitId, playbook: p.id }} arrow>
          {m.pb_use()}
        </MkLink>
      </div>
    </article>
  )
}

function Page() {
  const locale = getLocale() === 'id' ? 'id' : 'en'
  const { data, isLoading, isError, refetch, isFetching } = usePlaybooks()
  const playbooks = data?.playbooks ?? []
  const community = data?.community
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <section className="pb-10 pt-16 lg:pt-24">
          <Container className="flex flex-col items-center gap-6 text-center">
            <Overline>{m.pb_overline()}</Overline>
            <h1 className="max-w-[20ch]">{m.pb_title()}</h1>
            <p className="mk-lead max-w-[60ch]">{m.pb_sub()}</p>
          </Container>
        </section>

        <section className="pb-16">
          <Container className="flex flex-col gap-7">
            {isError ? <QueryError onRetry={refetch} retrying={isFetching} /> : isLoading ? (
              <LoadingState compact label={m.pb_loading()} />
            ) : playbooks.length ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {playbooks.map((p) => (
                  <Card key={`${p.source}-${p.id}`} p={p} locale={locale} />
                ))}
              </div>
            ) : (
              <div className="mk-card flex flex-col gap-2 p-8 text-center">
                <p className="mk-muted">{m.pb_empty()}</p>
              </div>
            )}
            <p className="mk-muted text-center text-[13px]" data-testid="playbook-community-status">
              {community?.ok ? m.pb_community_ok({ n: community.count }) : m.pb_community_off()}
            </p>
          </Container>
        </section>

        <section className="py-16 lg:py-20">
          <Container>
            <SectionHead
              title={m.pb_cta_title()}
              lead={m.pb_cta_desc()}
              actions={
                <>
                  <MkLink to="/plan" search={{ flow: 'new' }}>
                    {m.pb_cta_btn()}
                  </MkLink>
                  <MkLink to="/templates" search={{ tag: undefined }} variant="secondary" arrow>
                    {m.fw_tpl_title()}
                  </MkLink>
                </>
              }
            />
            <div className="mk-card mt-8 p-6 text-[14px] leading-relaxed">
              <div className="font-semibold">{m.pb_publish_title()}</div>
              <p className="mk-muted mt-1">{m.pb_publish_desc()}</p>
              <pre className="mt-3 overflow-x-auto rounded-lg border border-[var(--mk-border)] bg-[var(--mk-bg-soft,transparent)] p-3 text-[12px]">{`{
  "id": "warung-kopi",
  "name": { "id": "Warung kopi", "en": "Coffee stall" },
  "desc": { "id": "Menu, pesanan WhatsApp, kasir", "en": "Menu, WhatsApp orders, till" },
  "kitId": "simple-warung-umkm",
  "examples": { "id": ["Tambahkan menu musiman"], "en": ["Add a seasonal menu"] },
  "rules": [{ "kind": "ask", "templateId": "ask_price_change", "text": { "id": "Tanya dulu sebelum mengubah harga", "en": "Ask before changing a price" } }]
}`}</pre>
              <p className="mk-muted mt-2">
                <Link to="/docs" hash="agent-guide" className="underline-offset-2 hover:underline">
                  {m.pb_publish_docs()}
                </Link>
              </p>
            </div>
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}
